// Wishlist page: loads each saved product (by its stored URL + ".js") and renders a card grid
// with image, title, price, a link to the product and a remove button. Empty state otherwise.
const STORAGE_KEY = 'randaco:wishlist';

function readWishlist() {
  try {
    const list = JSON.parse(localStorage.getItem(STORAGE_KEY) || '[]');
    return Array.isArray(list) ? list.filter((url) => typeof url === 'string') : [];
  } catch (error) {
    return [];
  }
}

class SWishlistList extends HTMLElement {
  connectedCallback() {
    this.grid = this.querySelector('[data-wishlist-grid]');
    this.empty = this.querySelector('[data-wishlist-empty]');
    this.loading = this.querySelector('[data-wishlist-loading]');
    this.template = this.querySelector('template[data-wishlist-card]');
    this.formatter = new Intl.NumberFormat(this.dataset.locale || undefined, {
      style: 'currency',
      currency: this.dataset.currency || 'EUR',
    });
    this.onStorage = (event) => {
      if (event.key === STORAGE_KEY) this.load();
    };
    window.addEventListener('storage', this.onStorage);
    this.load();
  }

  disconnectedCallback() {
    window.removeEventListener('storage', this.onStorage);
  }

  async load() {
    const urls = readWishlist();
    this.grid.replaceChildren();
    if (!urls.length) return this.showEmpty(true);
    this.showEmpty(false);
    if (this.loading) this.loading.hidden = false;

    const products = await Promise.all(urls.map(async (url) => {
      try {
        const response = await fetch(`${url.split('?')[0]}.js`, { headers: { Accept: 'application/json' } });
        if (!response.ok) return { url, missing: true };
        return { url, data: await response.json() };
      } catch (error) {
        return { url, missing: true };
      }
    }));

    if (this.loading) this.loading.hidden = true;
    // Drop products that no longer exist (deleted or unpublished).
    const missing = products.filter((item) => item.missing).map((item) => item.url);
    if (missing.length) this.save(readWishlist().filter((url) => !missing.includes(url)));

    const available = products.filter((item) => item.data);
    if (!available.length) return this.showEmpty(true);
    available.forEach((item) => this.grid.append(this.card(item)));
  }

  card({ url, data }) {
    const fragment = this.template.content.cloneNode(true);
    const item = fragment.querySelector('li');
    fragment.querySelectorAll('[data-card-link]').forEach((link) => {
      link.href = url;
    });
    const img = fragment.querySelector('[data-card-image]');
    const imageUrl = data.featured_image || (data.images && data.images[0]);
    if (img && imageUrl) {
      const src = imageUrl.startsWith('//') ? `https:${imageUrl}` : imageUrl;
      const sized = new URL(src, window.location.href);
      sized.searchParams.set('width', '600');
      img.src = sized.toString();
      img.alt = data.title;
    } else if (img) {
      img.remove();
    }
    fragment.querySelector('[data-card-title]').textContent = data.title;
    const price = fragment.querySelector('[data-card-price]');
    const from = data.price_varies ? `${this.dataset.fromLabel} ` : '';
    price.textContent = `${from}${this.formatter.format(data.price / 100)}`;
    if (!data.available) {
      const soldOut = fragment.querySelector('[data-card-soldout]');
      if (soldOut) soldOut.hidden = false;
    }
    const remove = fragment.querySelector('[data-card-remove]');
    remove.setAttribute('aria-label', `${this.dataset.removeLabel}: ${data.title}`);
    remove.addEventListener('click', () => {
      this.save(readWishlist().filter((saved) => saved !== url));
      item.remove();
      if (!this.grid.children.length) this.showEmpty(true);
    });
    return fragment;
  }

  save(list) {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(list));
    } catch (error) {
      // Storage unavailable: nothing to persist.
    }
    window.dispatchEvent(new CustomEvent('wishlist:change', { detail: { list } }));
  }

  showEmpty(isEmpty) {
    if (this.empty) this.empty.hidden = !isEmpty;
    this.grid.hidden = isEmpty;
  }
}

if (!customElements.get('s-wishlist-list')) customElements.define('s-wishlist-list', SWishlistList);
